import React, { useState, useEffect } from 'react';
import InfoCard from '../InfoCard';
import './CardWrapper.css';
import methods from '../../data/methodsTransformed';
import example from '../../data/example';
import CodeMirror from '@uiw/react-codemirror';
import { javascript } from '@codemirror/lang-javascript';
import { oneDark } from '@codemirror/theme-one-dark';
import { SimpleTreeView, TreeItem } from '@mui/x-tree-view';
import { ExpandMore, ChevronRight } from '@mui/icons-material';
import { Box, Typography, Paper, Divider } from '@mui/material';

export default function CardWrapper({ selectedType, setShowEditor, showEditor }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredMethods, setFilteredMethods] = useState(methods);
  const [code, setCode] = useState(`// Editor de Expresiones n8n - Usa la sintaxis {{ }}

// Ejemplos de expresiones n8n:
{{ $json.nombre }}
{{ $json.edad }}
{{ $json.email }}
{{ $json.empresa.nombre }}
{{ $json.serviciosInteresados.length }}
{{ $json.serviciosInteresados[0] }}

// Expresiones más complejas:
{{ $json.nombre.toUpperCase() }}
{{ $json.edad > 30 ? "Senior" : "Junior" }}
{{ $json.serviciosInteresados.join(", ") }}`);
  const [consoleOutput, setConsoleOutput] = useState([]);
  const [isExecuting, setIsExecuting] = useState(false);
  const [highlightedPaths, setHighlightedPaths] = useState([]);
  const [expandedItems, setExpandedItems] = useState(['nombre', 'empresa', 'serviciosInteresados']);

  // Apply both type filtering and search filtering
  useEffect(() => {
    let results = methods;

    // Filter by type if a type is selected
    if (selectedType) {
      results = results.filter(method => method.datatype === selectedType);
    }

    // Filter by search term
    if (searchTerm.trim() !== '') {
      const normalizedSearchTerm = searchTerm.toLowerCase().trim();
      results = results.filter(method =>
        method.name.toLowerCase().includes(normalizedSearchTerm) ||
        (method.description && method.description.toLowerCase().includes(normalizedSearchTerm))
      );
    }

    setFilteredMethods(results);
  }, [selectedType, searchTerm]);

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const clearSearch = () => {
    console.log('clearSearch');
    setSearchTerm('');
  };

  const onChange = React.useCallback((value, viewUpdate) => {
    setCode(value);
  }, []);

  // Function to extract JSON paths from code (updated for n8n syntax)
  const extractJsonPaths = (code) => {
    const regex = /\{\{\s*\$json\.([a-zA-Z0-9\.\[\]_]+)/g;
    const paths = [];
    let match;
    
    while ((match = regex.exec(code)) !== null) {
      const fullPath = match[1];
      // Handle array indices like serviciosInteresados[0]
      const cleanPath = fullPath.replace(/\[\d+\]/g, '');
      const pathParts = cleanPath.split('.').filter(part => part.length > 0);
      
      if (pathParts.length > 0) {
        paths.push(pathParts);
        
        // Also add parent paths for auto-expansion
        for (let i = 1; i < pathParts.length; i++) {
          const parentPath = pathParts.slice(0, i);
          if (!paths.some(p => p.join('.') === parentPath.join('.'))) {
            paths.push(parentPath);
          }
        }
      }
    }
    
    console.log('Extracted paths:', paths);
    return paths;
  };

  const executeCode = () => {
    setIsExecuting(true);
    setConsoleOutput([]);

    // Create n8n-like context
    const $json = example[0];
    const logs = [];

    try {
      // Extract expressions from {{ }} blocks
      const expressionRegex = /\{\{\s*(.*?)\s*\}\}/g;
      const expressions = [];
      let match;

      while ((match = expressionRegex.exec(code)) !== null) {
        expressions.push({
          full: match[0],
          expression: match[1].trim()
        });
      }

      if (expressions.length === 0) {
        logs.push({ type: 'error', content: 'No se encontraron expresiones {{ }}' });
        logs.push({ type: 'info', content: 'Sintaxis: {{ $json.propiedad }}' });
      } else {
        expressions.forEach(({ full, expression }, index) => {
          try {
            // Create safe evaluation context
            const result = new Function('$json', `
              try {
                return ${expression};
              } catch (e) {
                throw new Error('Error en expresión: ' + e.message);
              }
            `)($json);

            // Format result for display
            let formattedResult;
            if (typeof result === 'object' && result !== null) {
              formattedResult = JSON.stringify(result, null, 2);
            } else if (typeof result === 'string') {
              formattedResult = `"${result}"`;
            } else {
              formattedResult = String(result);
            }

            logs.push({ type: 'expression', content: full });
            logs.push({ type: 'result', content: formattedResult });

          } catch (error) {
            logs.push({ type: 'expression', content: full });
            logs.push({ type: 'error', content: error.message });
          }
        });
      }

      setConsoleOutput(logs);

    } catch (error) {
      setConsoleOutput([{
        type: 'error',
        content: error.message
      }]);
    } finally {
      setIsExecuting(false);
    }
  };

  // Function to load code from card examples
  const handleLoadCodeExample = (exampleCode) => {
    setCode(exampleCode);
    setShowEditor(true);
    setConsoleOutput([]);
    
    // Extract JSON paths from the code
    const paths = extractJsonPaths(exampleCode);
    setHighlightedPaths(paths);
    
    // Auto-expand necessary nodes
    const pathsToExpand = paths.map(path => {
      const pathSegments = [];
      for (let i = 0; i < path.length; i++) {
        pathSegments.push(path.slice(0, i + 1).join('.'));
      }
      return pathSegments;
    }).flat();
    
    const uniqueExpandedItems = [...new Set([...expandedItems, ...pathsToExpand])];
    setExpandedItems(uniqueExpandedItems);
    
    console.log('Highlighting paths:', paths);
    console.log('Expanding items:', uniqueExpandedItems);
  };

  // Function to create TreeView structure from example data
  const createTreeItems = (obj, path = '') => {
    return Object.keys(obj).map((key) => {
      const value = obj[key];
      const currentPath = path ? `${path}.${key}` : key;
      const itemId = currentPath;

      // Check if this path should be highlighted
      const isHighlighted = highlightedPaths.some(highlightPath => 
        highlightPath.join('.') === currentPath
      );

      // Base styles for highlighted items
      const highlightStyles = isHighlighted ? {
        backgroundColor: 'rgba(124, 58, 237, 0.4) !important',
        border: '2px solid #8B5CF6',
        borderRadius: '6px',
        boxShadow: '0 0 0 2px rgba(139, 92, 246, 0.2)',
        '&:hover': {
          backgroundColor: 'rgba(124, 58, 237, 0.6) !important',
        }
      } : {};

      if (value === null) {
        return (
          <TreeItem 
            key={itemId} 
            itemId={itemId} 
            label={
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1.5, 
                py: 0.25,
                px: isHighlighted ? 1 : 0,
                borderRadius: isHighlighted ? '4px' : 0,
                ...highlightStyles
              }}>
                <Box sx={{ 
                  minWidth: '18px', 
                  height: '18px', 
                  borderRadius: '3px', 
                  bgcolor: isHighlighted ? '#8B5CF6' : '#6B7280', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  fontSize: '10px',
                  color: 'white',
                  boxShadow: isHighlighted ? '0 2px 8px rgba(139, 92, 246, 0.4)' : 'none'
                }}>
                  ∅
                </Box>
                <Typography variant="body2" sx={{ 
                  fontWeight: isHighlighted ? 600 : 500, 
                  color: isHighlighted ? '#E5E7EB' : '#D1D5DB', 
                  fontSize: '13px' 
                }}>
                  {key}:
                </Typography>
                <Typography variant="body2" sx={{ 
                  color: '#9CA3AF', 
                  fontSize: '13px', 
                  fontStyle: 'italic',
                  fontWeight: isHighlighted ? 500 : 'normal'
                }}>
                  null
                </Typography>
              </Box>
            }
          />
        );
      }

      if (Array.isArray(value)) {
        return (
          <TreeItem 
            key={itemId} 
            itemId={itemId} 
            label={
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1.5, 
                py: 0.25,
                px: isHighlighted ? 1 : 0,
                borderRadius: isHighlighted ? '4px' : 0,
                ...highlightStyles
              }}>
                <Box sx={{ 
                  minWidth: '18px', 
                  height: '18px', 
                  borderRadius: '3px', 
                  bgcolor: isHighlighted ? '#8B5CF6' : '#3B82F6', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  fontSize: '10px',
                  color: 'white',
                  fontWeight: 'bold',
                  boxShadow: isHighlighted ? '0 2px 8px rgba(139, 92, 246, 0.4)' : 'none'
                }}>
                  [ ]
                </Box>
                <Typography variant="body2" sx={{ 
                  fontWeight: isHighlighted ? 600 : 500, 
                  color: isHighlighted ? '#E5E7EB' : '#D1D5DB', 
                  fontSize: '13px' 
                }}>
                  {key}:
                </Typography>
                <Typography variant="body2" sx={{ 
                  color: isHighlighted ? '#A78BFA' : '#60A5FA', 
                  fontSize: '13px',
                  fontWeight: isHighlighted ? 500 : 'normal'
                }}>
                  Array[{value.length}]
                </Typography>
              </Box>
            }
          >
            {value.map((item, index) => {
              const indexPath = `${currentPath}[${index}]`;
              if (typeof item === 'object' && item !== null) {
                return (
                  <TreeItem
                    key={indexPath}
                    itemId={indexPath}
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.25 }}>
                        <Box sx={{ 
                          minWidth: '16px', 
                          height: '16px', 
                          borderRadius: '3px', 
                          bgcolor: '#F59E0B', 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'center',
                          fontSize: '9px',
                          color: 'white',
                          fontWeight: 'bold'
                        }}>
                          {index}
                        </Box>
                        <Typography variant="body2" sx={{ color: '#FBBF24', fontSize: '13px' }}>
                          Object
                        </Typography>
                      </Box>
                    }
                  >
                    {createTreeItems(item, indexPath)}
                  </TreeItem>
                );
              } else {
                return (
                  <TreeItem
                    key={indexPath}
                    itemId={indexPath}
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.25 }}>
                        <Box sx={{ 
                          minWidth: '16px', 
                          height: '16px', 
                          borderRadius: '3px', 
                          bgcolor: '#F59E0B', 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'center',
                          fontSize: '9px',
                          color: 'white',
                          fontWeight: 'bold'
                        }}>
                          {index}
                        </Box>
                        <Typography variant="body2" sx={{ 
                          color: '#10B981', 
                          fontFamily: 'monospace',
                          fontSize: '13px'
                        }}>
                          {typeof item === 'string' ? `"${item}"` : String(item)}
                        </Typography>
                      </Box>
                    }
                  />
                );
              }
            })}
          </TreeItem>
        );
      }

      if (typeof value === 'object') {
        return (
          <TreeItem 
            key={itemId} 
            itemId={itemId} 
            label={
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1.5, 
                py: 0.25,
                px: isHighlighted ? 1 : 0,
                borderRadius: isHighlighted ? '4px' : 0,
                ...highlightStyles
              }}>
                <Box sx={{ 
                  minWidth: '18px', 
                  height: '18px', 
                  borderRadius: '3px', 
                  bgcolor: isHighlighted ? '#8B5CF6' : '#F59E0B', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  fontSize: '10px',
                  color: 'white',
                  fontWeight: 'bold',
                  boxShadow: isHighlighted ? '0 2px 8px rgba(139, 92, 246, 0.4)' : 'none'
                }}>
                  { }
                </Box>
                <Typography variant="body2" sx={{ 
                  fontWeight: isHighlighted ? 600 : 500, 
                  color: isHighlighted ? '#E5E7EB' : '#D1D5DB', 
                  fontSize: '13px' 
                }}>
                  {key}:
                </Typography>
                <Typography variant="body2" sx={{ 
                  color: isHighlighted ? '#DDD6FE' : '#FBBF24', 
                  fontSize: '13px',
                  fontWeight: isHighlighted ? 500 : 'normal'
                }}>
                  Object
                </Typography>
              </Box>
            }
          >
            {createTreeItems(value, currentPath)}
          </TreeItem>
        );
      }

      // Primitive values
      const getTypeInfo = (val) => {
        if (typeof val === 'string') return { 
          color: isHighlighted ? '#A7F3D0' : '#10B981', 
          icon: 'Aa',
          iconBg: isHighlighted ? '#8B5CF6' : '#10B981'
        };
        if (typeof val === 'number') return { 
          color: isHighlighted ? '#FCA5A5' : '#EF4444', 
          icon: '#',
          iconBg: isHighlighted ? '#8B5CF6' : '#EF4444'
        };
        if (typeof val === 'boolean') return { 
          color: isHighlighted ? '#DDD6FE' : '#A78BFA', 
          icon: val ? '✓' : '✗',
          iconBg: '#8B5CF6'
        };
        return { 
          color: '#9CA3AF', 
          icon: '?',
          iconBg: '#6B7280'
        };
      };

      const formatValue = (val) => {
        if (typeof val === 'string') return `"${val}"`;
        return String(val);
      };

      const typeInfo = getTypeInfo(value);

      return (
        <TreeItem 
          key={itemId} 
          itemId={itemId} 
          label={
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1.5, 
              py: 0.25,
              px: isHighlighted ? 1 : 0,
              borderRadius: isHighlighted ? '4px' : 0,
              ...highlightStyles
            }}>
              <Box sx={{ 
                minWidth: '18px', 
                height: '18px', 
                borderRadius: '3px', 
                bgcolor: typeInfo.iconBg,
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                fontSize: '10px',
                color: 'white',
                fontWeight: 'bold',
                boxShadow: isHighlighted ? '0 2px 8px rgba(139, 92, 246, 0.4)' : 'none'
              }}>
                {typeInfo.icon}
              </Box>
              <Typography variant="body2" sx={{ 
                fontWeight: isHighlighted ? 600 : 500, 
                color: isHighlighted ? '#E5E7EB' : '#D1D5DB', 
                fontSize: '13px' 
              }}>
                {key}:
              </Typography>
              <Typography variant="body2" sx={{ 
                color: typeInfo.color, 
                fontFamily: 'monospace',
                fontSize: '13px',
                fontWeight: isHighlighted ? 500 : 'normal'
              }}>
                {formatValue(value)}
              </Typography>
            </Box>
          }
        />
      );
    });
  };

  return (
    <div className="bg-gray-50 dark:bg-gray-900">
      {/* Prominent Search Bar */}
      <div className="bg-indigo-100 dark:bg-gray-800 py-6 px-8 border-t border-b border-indigo-200 dark:border-gray-700 sticky top-0 z-20 shadow-md">
        <div className="max-w-7xl mx-auto">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
              <svg className="h-6 w-6 text-indigo-600 dark:text-indigo-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8"></circle>
                <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
              </svg>
            </div>
            <input
              type="text"
              placeholder="Buscar por nombre o descripción..."
              value={searchTerm}
              onChange={handleSearch}
              className="block w-full pl-12 pr-12 py-3 rounded-lg bg-white dark:bg-gray-700 border-2 border-indigo-300 dark:border-indigo-600
                        text-gray-700 dark:text-white placeholder-gray-500 dark:placeholder-gray-300
                        focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 focus:border-transparent
                        text-lg shadow-sm transition-all duration-300"
            />
            {searchTerm && (
              <button
                onClick={clearSearch}
                className="absolute inset-y-0 right-0 r-4 flex items-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
              >
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
              </button>
            )}
          </div>

          {searchTerm && (
            <div className="mt-2 text-indigo-700 dark:text-indigo-300 font-medium">
              {filteredMethods.length === 0
                ? `No se encontraron resultados para "${searchTerm}"`
                : `Se encontraron ${filteredMethods.length} resultado${filteredMethods.length !== 1 ? 's' : ''} para "${searchTerm}"`}
            </div>
          )}
        </div>
      </div>

      {/* Fixed Code Editor Button */}
      <div className="fixed top-30  z-30">
          <button
            onClick={() => setShowEditor(prev => !prev)}
            className={`
            group flex items-center gap-2 px-6 py-3 rounded-lg shadow-lg
            transition-all duration-300 ease-in-out transform hover:scale-105
              ${showEditor 
              ? 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white shadow-xl' 
                : 'bg-gray-800 hover:bg-gradient-to-r hover:from-purple-600 hover:to-indigo-600 text-gray-300 hover:text-white'}
            `}
          >
            <i className={`fas ${showEditor ? 'fa-chevron-down' : 'fa-code'} text-lg transition-transform duration-300 ${showEditor ? 'rotate-180' : ''}`}></i>
          <span className="font-medium">{showEditor ? 'Cerrar' : 'Editor'}</span>
          </button>
        </div>

      {/* Full-Width Two-Section Editor */}
      {showEditor ? (
        <div className="h-screen bg-gray-900 overflow-hidden">
          <div className="max-w-7xl mx-auto px-8 py-6">
            <div className="grid grid-cols-12 gap-6 ]">
              
              {/* Left Section - Data Tree */}
              <div className="col-span-4  h-[calc(100vh-150px)] flex flex-col">
                <Paper elevation={3} sx={{ 
                  height: '100%', 
                  bgcolor: '#1F2937', 
                  border: '1px solid #374151', 
                  overflow: 'hidden'
                }} className="flex flex-col">
                  
                  {/* Scrollable Content */}
                  <div className="flex-1 overflow-y-scroll p-2 scrollbar-thin scrollbar-thumb-gray-500 scrollbar-track-gray-700">
                    <SimpleTreeView
                      slots={{
                        collapseIcon: ExpandMore,
                        expandIcon: ChevronRight,
                      }}
                      slotProps={{
                        collapseIcon: { sx: { color: '#9CA3AF', fontSize: '16px' } },
                        expandIcon: { sx: { color: '#9CA3AF', fontSize: '16px' } },
                      }}
                      expandedItems={expandedItems}
                      onExpandedItemsChange={(event, itemIds) => setExpandedItems(itemIds)}
                      sx={{
                        '& .MuiTreeItem-root': {
                          '& .MuiTreeItem-content': {
                            padding: '4px 8px',
                            borderRadius: '4px',
                            marginBottom: '1px',
                            '&:hover': {
                              backgroundColor: 'rgba(75, 85, 99, 0.3)',
                            },
                            '&.Mui-selected': {
                              backgroundColor: 'rgba(75, 85, 99, 0.5) !important',
                            }
                          },
                          '& .MuiTreeItem-group': {
                            marginLeft: '20px',
                            paddingLeft: '16px',
                            borderLeft: '2px solid #374151',
                            position: 'relative',
                            '&::before': {
                              content: '""',
                              position: 'absolute',
                              left: '-1px',
                              top: 0,
                              bottom: 0,
                              width: '2px',
                              background: 'linear-gradient(to bottom, #6B7280 0%, transparent 100%)',
                              opacity: 0.6
                            }
                          }
                        }
                      }}
                    >
                      {createTreeItems(example[0])}
                    </SimpleTreeView>
                  </div>
                </Paper>
                </div>

              {/* Right Section - Code Editor and Third Section */}
              <div className="col-span-8 h-full flex flex-col gap-6">
                
                {/* Code Editor Section */}
                <div className="h-2/3">
                  <Paper elevation={3} sx={{ height: '100%', bgcolor: '#1F2937', border: '1px solid #7C3AED' }}>
                    {/* Editor Header */}
                    <Box sx={{ p: 2, borderBottom: '1px solid #7C3AED', bgcolor: '#111827' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#EF4444' }}></Box>
                            <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#F59E0B' }}></Box>
                            <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#10B981' }}></Box>
                          </Box>
               
                        </Box>
                  <button 
                          className="px-3 py-1.5 text-sm bg-purple-600 hover:bg-purple-700 text-white rounded transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    onClick={executeCode}
                    disabled={isExecuting}
                  >
                          <i className={`fas ${isExecuting ? 'fa-spinner fa-spin' : 'fa-play'}`}></i>
                    {isExecuting ? 'Ejecutando...' : 'Ejecutar'}
                  </button>
                      </Box>
                    </Box>

            {/* Editor Content */}
                    <Box sx={{ height: 'calc(100% - 70px)', display: 'flex', flexDirection: 'column' }}>
                      <Box sx={{ flex: '1 1 65%', p: 2 }}>
              <CodeMirror
                value={code}
                          height="100%"
                theme={oneDark}
                extensions={[javascript({ jsx: true })]}
                onChange={onChange}
                          className="rounded-lg overflow-hidden h-full"
                basicSetup={{
                  lineNumbers: true,
                  highlightActiveLineGutter: true,
                  highlightSpecialChars: true,
                  history: true,
                  foldGutter: true,
                  drawSelection: true,
                  dropCursor: true,
                  allowMultipleSelections: true,
                  indentOnInput: true,
                  syntaxHighlighting: true,
                  bracketMatching: true,
                  closeBrackets: true,
                  autocompletion: true,
                  rectangularSelection: true,
                  crosshairCursor: true,
                  highlightActiveLine: true,
                  highlightSelectionMatches: true,
                          }}
                        />
                      </Box>
                      
                      <Divider sx={{ bgcolor: '#374151' }} />
                      
                      {/* Console Output */}
                      <Box sx={{ flex: '1 1 35%', bgcolor: '#111827', p: 2, overflow: 'auto' }}>
                        <Typography variant="subtitle2" sx={{ color: '#9CA3AF', mb: 1, borderBottom: '1px solid #374151', pb: 1, fontSize: '12px' }}>
                          Output
                        </Typography>
                        <Box sx={{ fontFamily: 'monospace', fontSize: '12px' }}>
                  {consoleOutput.length > 0 ? (
                            <Box>
                      {consoleOutput.map((output, index) => {
                        const getOutputStyle = () => {
                          switch(output.type) {
                            case 'error':
                              return { color: '#EF4444', prefix: '[ERROR]' };
                            case 'expression':
                              return { color: '#60A5FA', prefix: '[EXPR]' };
                            case 'result':
                              return { color: '#10B981', prefix: '[OUT]' };
                            case 'info':
                              return { color: '#9CA3AF', prefix: '[INFO]' };
                            default:
                              return { color: '#D1D5DB', prefix: '[LOG]' };
                          }
                        };

                        const style = getOutputStyle();

                        return (
                                <Typography
                          key={index}
                                  variant="body2"
                                  sx={{
                                    color: style.color,
                                    fontFamily: 'monospace',
                                    mb: 0.5,
                                    fontSize: '12px',
                                    whiteSpace: 'pre-wrap'
                                  }}
                                >
                            <span style={{ opacity: 0.6, marginRight: '8px' }}>{style.prefix}</span>
                                  {output.content}
                                </Typography>
                        );
                      })}
                            </Box>
                          ) : (
                            <Typography variant="body2" sx={{ color: '#6B7280', fontStyle: 'italic', fontSize: '12px' }}>
                              Esperando ejecución...
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </Box>
                  </Paper>
                </div>

                {/* Third Section - Placeholder for new content */}


              </div>
            </div>
          </div>
        </div>
      ) : (
        // Normal Cards Grid when editor is closed
        <div className="max-w-7xl mx-auto px-8">
        <div className="container mx-auto px-8 py-8">
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
            {filteredMethods && filteredMethods.map((method, index) => (
              <div key={index} className="w-full">
                  <InfoCard 
                    itemData={method} 
                    onLoadCodeExample={handleLoadCodeExample}
                  />
              </div>
            ))}
          </div>

          {filteredMethods.length === 0 && (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <svg className="w-16 h-16 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
              </svg>
              <h3 className="text-xl font-semibold text-gray-700 dark:text-gray-300 mb-2">No se encontraron métodos</h3>
              <p className="text-gray-500 dark:text-gray-400">
                {searchTerm ? 'Intenta con otros términos de búsqueda' : 'Intenta seleccionar otro tipo de datos'}
              </p>
            </div>
          )}
        </div>
      </div>
      )}
    </div>
  );
}
